<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import axios from 'axios';
import { useAlert } from 'dashboard/composables';
import { zonedTimeToUtc } from 'date-fns-tz';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import DateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';
import WhatsAppEditor from 'dashboard/routes/dashboard/bulkmessage/components/WhatsAppEditor.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import WhatsappTemplatesModal from 'dashboard/components/widgets/conversation/WhatsappTemplates/Modal.vue';

const RECURRENCE_TYPES = {
  NO_RECURRENCE: 'no_recurrence',
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  CUSTOM_INTERVAL: 'custom_interval',
};

const getInitialScheduleTime = () => {
  const date = new Date();
  // Just add 1 minute to ensure it's in the future
  date.setMinutes(date.getMinutes() + 1);
  date.setMilliseconds(0);
  return date;
};

const DAYS_OF_WEEK = [
  { id: 1, label: 'Segunda', shortLabel: 'Seg' },
  { id: 2, label: 'Terça', shortLabel: 'Ter' },
  { id: 3, label: 'Quarta', shortLabel: 'Qua' },
  { id: 4, label: 'Quinta', shortLabel: 'Qui' },
  { id: 5, label: 'Sexta', shortLabel: 'Sex' },
  { id: 6, label: 'Sábado', shortLabel: 'Sáb' },
  { id: 0, label: 'Domingo', shortLabel: 'Dom' },
];

export default {
  components: {
    DateTimePicker,
    WhatsAppEditor,
    ConfirmationModal,
    WhatsappTemplatesModal,
  },
  mixins: [fileUploadMixin],
  props: {
    targetScope: {
      type: String,
      default: 'conversation',
      validator: value => ['conversation', 'broadcast'].includes(value),
    },
    conversationId: {
      type: Number,
      default: null,
    },
    initialContent: {
      type: String,
      default: '',
    },
    initialAttachments: {
      type: Array,
      default: () => [],
    },
    inboxId: {
      type: Number,
      default: null,
    },
  },
  emits: ['close', 'scheduled'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const baseDate = getInitialScheduleTime();

    // Extract signed IDs from initial attachments
    const initialSignedIds = this.initialAttachments
      .map(att => att.blobSignedId)
      .filter(id => id);

    return {
      show: true,
      scheduledAt: baseDate,
      timezone: detectedTimezone || 'America/Sao_Paulo',
      recurrenceType: RECURRENCE_TYPES.NO_RECURRENCE,
      content: this.initialContent,
      attachments: [...this.initialAttachments],
      attachmentSignedIds: initialSignedIds,
      // Armazena File objects pendentes que serão enviados no submit (modo indireto)
      pendingAttachmentFiles: {},
      isPreparingAttachments: false,
      // Recurrence config
      selectedDaysOfWeek: [],
      dayOfMonth: 1,
      intervalDays: 1,
      // Broadcast specific
      targetPayload: {},
      selectedSegmentIds: [],
      selectedContactIds: [],
      // UI state
      errors: [],
      isSubmitting: false,
      showAdvanced: false,
      isDragging: false,
      dragCounter: 0,
      showTemplateVariables: false,
      showEmojiPicker: false,
      // WhatsApp template state
      templateMode: false,
      selectedTemplate: null,
      showWhatsappTemplatesModal: false,
      templateMediaFile: null,
      // Cached UTC date for the selected timezone
      scheduledAtUtc: null,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'scheduledMessages/getUIFlags',
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
    }),
    RECURRENCE_TYPES() {
      return RECURRENCE_TYPES;
    },
    DAYS_OF_WEEK() {
      return DAYS_OF_WEEK;
    },
    isRecurrent() {
      return this.recurrenceType !== RECURRENCE_TYPES.NO_RECURRENCE;
    },
    isWeeklyRecurrence() {
      return this.recurrenceType === RECURRENCE_TYPES.WEEKLY;
    },
    isMonthlyRecurrence() {
      return this.recurrenceType === RECURRENCE_TYPES.MONTHLY;
    },
    isCustomInterval() {
      return this.recurrenceType === RECURRENCE_TYPES.CUSTOM_INTERVAL;
    },
    isBroadcast() {
      return this.targetScope === 'broadcast';
    },
    timezoneOptions() {
      const getGMTOffset = (timezone) => {
        const date = this.scheduledAt || new Date();

        try {
          // Use Intl.DateTimeFormat to get the timezone offset
          const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'longOffset'
          });

          const parts = formatter.formatToParts(date);
          const offsetPart = parts.find(part => part.type === 'timeZoneName');

          if (offsetPart && offsetPart.value) {
            // Extract offset (e.g., "GMT-03:00" -> "GMT-3")
            return offsetPart.value.replace(/GMT([+-])0?(\d+):00/, 'GMT$1$2');
          }
        } catch (e) {
          // Fallback for browsers that don't support longOffset
        }

        // Manual fallback: calculate offset in hours
        const utcDate = Date.UTC(
          date.getUTCFullYear(),
          date.getUTCMonth(),
          date.getUTCDate(),
          date.getUTCHours(),
          date.getUTCMinutes(),
          date.getUTCSeconds()
        );

        const tzString = date.toLocaleString('en-US', { timeZone: timezone });
        const tzDate = new Date(tzString);
        const offset = Math.round((tzDate.getTime() - utcDate) / (1000 * 60 * 60));

        return offset === 0 ? 'GMT' : `GMT${offset > 0 ? '+' : ''}${offset}`;
      };

      return [
        { value: 'America/Sao_Paulo', label: `São Paulo (${getGMTOffset('America/Sao_Paulo')})` },
        { value: 'America/Manaus', label: `Manaus (${getGMTOffset('America/Manaus')})` },
        { value: 'America/Recife', label: `Recife (${getGMTOffset('America/Recife')})` },
        { value: 'America/Noronha', label: `Fernando de Noronha (${getGMTOffset('America/Noronha')})` },
        { value: 'UTC', label: 'UTC (GMT)' },
      ];
    },
    hasAudioAttachment() {
      return this.attachments.some(att => {
        const type = att.resource?.content_type || att.resource?.type || '';
        return type.includes('audio');
      });
    },
    submitButtonText() {
      if (this.isSubmitting) {
        return 'Agendando...';
      }
      return this.isBroadcast ? 'Agendar Disparo' : 'Agendar Mensagem';
    },
    minScheduleDateTime() {
      // Just needs to be in the future, no minimum delay
      const minDate = new Date();
      minDate.setMilliseconds(0);
      return minDate;
    },
    schedulePreviewText() {
      const scheduledUtc = this.getScheduledAtUtc();
      if (!scheduledUtc) return '';

      const options = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: this.timezone
      };

      const formatted = new Intl.DateTimeFormat('pt-BR', options).format(scheduledUtc);

      // Calculate relative time
      const now = new Date();
      const diff = scheduledUtc - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(hours / 24);

      let relative = '';
      if (days > 0) {
        relative = `daqui a ${days} dia${days > 1 ? 's' : ''}`;
        const remainingHours = hours % 24;
        if (remainingHours > 0) {
          relative += ` e ${remainingHours} hora${remainingHours > 1 ? 's' : ''}`;
        }
      } else if (hours > 0) {
        relative = `daqui a ${hours} hora${hours > 1 ? 's' : ''}`;
      } else {
        const minutes = Math.floor(diff / (1000 * 60));
        relative = `daqui a ${minutes} minuto${minutes > 1 ? 's' : ''}`;
      }

      return { formatted, relative };
    },
    recurrenceCards() {
      return [
        {
          value: RECURRENCE_TYPES.NO_RECURRENCE,
          icon: 'i-lucide-calendar-check',
          label: 'Uma vez',
          description: 'Envio único'
        },
        {
          value: RECURRENCE_TYPES.DAILY,
          icon: 'i-lucide-calendar-clock',
          label: 'Diário',
          description: 'Todo dia'
        },
        {
          value: RECURRENCE_TYPES.WEEKLY,
          icon: 'i-lucide-calendar-days',
          label: 'Semanal',
          description: 'Dias fixos'
        },
        {
          value: RECURRENCE_TYPES.MONTHLY,
          icon: 'i-lucide-calendar',
          label: 'Mensal',
          description: 'Dia do mês'
        },
        {
          value: RECURRENCE_TYPES.CUSTOM_INTERVAL,
          icon: 'i-lucide-calendar-range',
          label: 'Intervalo',
          description: 'A cada X dias'
        },
      ];
    },
    templateVariables() {
      if (!this.isBroadcast && this.currentChat) {
        // For conversation scope, use contact from current chat
        const contact = this.currentChat.meta?.sender;
        return [
          {
            name: '{{nome}}',
            description: contact?.name?.split(' ')[0] || 'Nome do contato'
          },
          {
            name: '{{sobrenome}}',
            description: contact?.name?.split(' ').slice(1).join(' ') || 'Sobrenome'
          },
          {
            name: '{{nome_completo}}',
            description: contact?.name || 'Nome completo do contato'
          },
          {
            name: '{{telefone}}',
            description: contact?.phone_number || 'Número de telefone'
          },
        ];
      }
      // For broadcast or when no chat, show generic variables
      return [
        { name: '{{nome}}', description: 'Nome do contato' },
        { name: '{{sobrenome}}', description: 'Sobrenome do contato' },
        { name: '{{nome_completo}}', description: 'Nome completo do contato' },
        { name: '{{telefone}}', description: 'Número de telefone do contato' },
      ];
    },
    isWhatsAppInbox() {
      // Check if the inbox is WhatsApp
      const inbox = this.$store.getters['inboxes/getInbox'](this.inboxId);
      return inbox?.channel_type === 'Channel::Whatsapp';
    },
    canSendRegularMessage() {
      // For broadcast, always allow both
      if (this.isBroadcast) return true;

      // For conversation, check can_reply
      return this.currentChat?.can_reply !== false;
    },
    mustUseTemplate() {
      return true
      //return !this.canSendRegularMessage && !this.isBroadcast && this.isWhatsAppInbox;
    },
  },
  validations: {
    scheduledAt: { required },
    content: {
      required(value) {
        return value || this.attachments.length > 0;
      },
    },
  },
  watch: {
    hasAudioAttachment(newValue) {
      if (newValue && (this.content || this.attachments.length > 1)) {
        useAlert('⚠️ Áudio não pode ser combinado com texto ou outros anexos (restrição WhatsApp)');
      }
    },
  },
  created() {
    this.initializeAttachmentState();
  },
  mounted() {
    this.setupDragAndDrop();
    // Não fazer upload automático - anexos serão enviados no submit (modo indireto)
    // Anexos vindos da ReplyBox já têm blobSignedId preenchido no data()

    // Force template mode for WhatsApp inboxes
    if (this.isWhatsAppInbox) {
      this.templateMode = true;
    }
  },
  beforeUnmount() {
    this.removeDragAndDrop();
  },
  methods: {
    isImageFile(type) {
      return type?.startsWith('image/');
    },
    isVideoFile(type) {
      return type?.startsWith('video/');
    },
    isAudioFile(type) {
      return type?.startsWith('audio/');
    },
    formatFileSize(sizeInBytes) {
      if (sizeInBytes < 1024) {
        return `${sizeInBytes} B`;
      }
      if (sizeInBytes < 1024 * 1024) {
        return `${(sizeInBytes / 1024).toFixed(1)} KB`;
      }
      return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    setQuickTime(preset) {
      const now = new Date();
      let targetDate = new Date();

      switch(preset) {
        case '1h':
          targetDate.setHours(now.getHours() + 1);
          targetDate.setMinutes(0);
          break;
        case '3h':
          targetDate.setHours(now.getHours() + 3);
          targetDate.setMinutes(0);
          break;
        case 'tomorrow_9':
          targetDate.setDate(now.getDate() + 1);
          targetDate.setHours(9, 0);
          break;
        case 'tomorrow_14':
          targetDate.setDate(now.getDate() + 1);
          targetDate.setHours(14, 0);
          break;
        case 'next_monday':
          const daysUntilMonday = (8 - now.getDay()) % 7 || 7;
          targetDate.setDate(now.getDate() + daysUntilMonday);
          targetDate.setHours(9, 0);
          break;
      }

      targetDate.setMilliseconds(0);
      this.scheduledAt = targetDate;
    },
    onClose() {
      this.show = false;
      this.templateMediaFile = null;
      this.$emit('close');
    },
    disabledDate(date) {
      // Disable any date/time in the past
      const now = new Date();
      return date < now;
    },
    toggleDayOfWeek(dayId) {
      const index = this.selectedDaysOfWeek.indexOf(dayId);
      if (index === -1) {
        this.selectedDaysOfWeek.push(dayId);
      } else {
        this.selectedDaysOfWeek.splice(index, 1);
      }
    },
    isDaySelected(dayId) {
      return this.selectedDaysOfWeek.includes(dayId);
    },
    setupDragAndDrop() {
      const messageArea = this.$refs.messageArea;
      if (!messageArea) return;

      messageArea.addEventListener('dragenter', this.handleDragEnter);
      messageArea.addEventListener('dragleave', this.handleDragLeave);
      messageArea.addEventListener('dragover', this.handleDragOver);
      messageArea.addEventListener('drop', this.handleDrop);
    },
    removeDragAndDrop() {
      const messageArea = this.$refs.messageArea;
      if (!messageArea) return;

      messageArea.removeEventListener('dragenter', this.handleDragEnter);
      messageArea.removeEventListener('dragleave', this.handleDragLeave);
      messageArea.removeEventListener('dragover', this.handleDragOver);
      messageArea.removeEventListener('drop', this.handleDrop);
    },
    handleDragEnter(e) {
      e.preventDefault();
      e.stopPropagation();
      this.dragCounter++;
      this.isDragging = true;
    },
    handleDragLeave(e) {
      e.preventDefault();
      e.stopPropagation();
      this.dragCounter--;
      if (this.dragCounter === 0) {
        this.isDragging = false;
      }
    },
    handleDragOver(e) {
      e.preventDefault();
      e.stopPropagation();
    },
    handleDrop(e) {
      e.preventDefault();
      e.stopPropagation();
      this.dragCounter = 0;
      this.isDragging = false;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        const fileWrapper = { file };
        this.onFileUpload(fileWrapper);
      }
    },
    // Sobrescreve o mixin para SEMPRE usar modo indireto no modal de agendamento
    // Isso evita uploads prematuros - o arquivo só será enviado no submit
    onFileUpload(file) {
      if (!file) return;
      // Forçar caminho indireto - não chamar DirectUpload
      this.onIndirectFileUpload(file);
    },
    initializeAttachmentState() {
      if (!this.attachments || !this.attachments.length) {
        return;
      }

      const now = Date.now();
      this.attachments = this.attachments.map((attachment, index) => {
        if (!attachment) {
          return attachment;
        }

        if (!attachment.id) {
          attachment.id = now + index;
        }

        const signedId = this.extractExistingSignedId(attachment);
        if (signedId) {
          if (!this.attachmentSignedIds.includes(signedId)) {
            this.attachmentSignedIds.push(signedId);
          }
        } else {
          const file = this.extractFileFromAttachment(attachment);
          if (file instanceof File) {
            this.pendingAttachmentFiles = {
              ...this.pendingAttachmentFiles,
              [attachment.id]: file,
            };
          }
        }

        return attachment;
      });
    },
    attachFile({ file, blob }) {
      if (this.attachments.length >= 1) {
        useAlert('Máximo de 1 anexo permitido');
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        const attachmentId = new Date().getTime();
        const attachment = {
          id: attachmentId,
          resource: file.file,
          thumb: reader.result,
          isRecordedAudio: false,
        };

        this.attachments.push(attachment);

        // Se já veio com signed_id (ex: anexo vindo da ReplyBox), guardar
        if (blob && blob.signed_id) {
          attachment.blobSignedId = blob.signed_id;
          this.attachmentSignedIds.push(blob.signed_id);
        } else {
          // Guardar o File para envio posterior no submit (modo indireto)
          this.pendingAttachmentFiles[attachmentId] = file.file;
        }
      };
    },
    removeAttachment(index) {
      const attachment = this.attachments[index];
      if (attachment) {
        // Limpar do pendingAttachmentFiles se existir
        delete this.pendingAttachmentFiles[attachment.id];
        // Limpar signed_id se existir
        if (attachment.blobSignedId) {
          const signedIdIndex = this.attachmentSignedIds.indexOf(attachment.blobSignedId);
          if (signedIdIndex > -1) {
            this.attachmentSignedIds.splice(signedIdIndex, 1);
          }
        }
      }
      this.attachments.splice(index, 1);
    },
    showFileSizeError(message) {
      useAlert(message);
    },
    async prepareInitialAttachmentSignedIds() {
      if (!this.attachments.length) {
        this.attachmentSignedIds = [];
        return;
      }

      const attachmentsNeedingUpload = this.attachments.filter(
        attachment => !this.extractExistingSignedId(attachment)
      );

      if (!attachmentsNeedingUpload.length) {
        this.attachmentSignedIds = this.attachments
          .map(attachment => this.extractExistingSignedId(attachment))
          .filter(id => id);
        return;
      }

      this.isPreparingAttachments = true;
      const resolvedIds = [];

      try {
        for (let index = 0; index < this.attachments.length; index += 1) {
          const attachment = this.attachments[index];
          let signedId = this.extractExistingSignedId(attachment);
          if (!signedId) {
            signedId = await this.uploadAttachmentForSignedId(attachment);
          }

          if (!signedId) {
            throw new Error('ATTACHMENT_SIGNED_ID_MISSING');
          }

          resolvedIds[index] = signedId;
          if (this.attachments[index]) {
            this.attachments[index].blobSignedId = signedId;
          }
        }

        this.attachmentSignedIds = resolvedIds;
      } catch (error) {
        console.error('[ScheduleMessageModal] Failed to prepare attachments', error);
        const message = 'Erro ao preparar anexos. Faça o upload novamente dentro do modal.';
        if (!this.errors.includes(message)) {
          this.errors.push(message);
        }
        useAlert(message);
      } finally {
        this.isPreparingAttachments = false;
      }
    },
    extractExistingSignedId(attachment) {
      if (!attachment) {
        return null;
      }

      return (
        attachment.blobSignedId ||
        attachment.signed_id ||
        attachment.signedId ||
        attachment.resource?.blobSignedId ||
        attachment.resource?.signed_id ||
        attachment.resource?.signedId ||
        null
      );
    },
    extractFileFromAttachment(attachment) {
      if (!attachment) {
        return null;
      }

      if (attachment.resource instanceof File) {
        return attachment.resource;
      }

      if (attachment.resource?.file instanceof File) {
        return attachment.resource.file;
      }

      return null;
    },
    uploadAttachmentForSignedId(attachment) {
      const file = this.extractFileFromAttachment(attachment);
      if (!file) {
        return Promise.resolve(null);
      }

      return this.directUploadFile(file).then(blob => {
        attachment.blobSignedId = blob.signed_id;
        return blob.signed_id;
      });
    },
    async uploadViaMultipart(file) {
      if (!this.accountId) {
        throw new Error('missing_account_id');
      }

      const uploadUrl = `/api/v1/accounts/${this.accountId}/bulk_messages/uploads`;
      const formData = new FormData();
      formData.append('file', file);

      const headers = {};
      if (this.currentUser?.access_token) {
        headers['api_access_token'] = this.currentUser.access_token;
      }

      const response = await axios.post(uploadUrl, formData, {
        headers,
        withCredentials: true,
      });

      return response.data;
    },
    async directUploadFile(file) {
      return this.uploadViaMultipart(file);
    },
    buildTemplateMediaUrl(blob) {
      if (!blob?.signed_id) return null;
      const origin = window?.location?.origin || '';
      return `${origin}/rails/active_storage/blobs/${blob.signed_id}/${blob.filename}`;
    },
    deriveTemplateMediaType(file) {
      const mimeType = file?.type || '';
      if (mimeType.startsWith('image/')) return 'image';
      if (mimeType.startsWith('video/')) return 'video';
      return 'document';
    },
    async applyTemplateMediaToTemplateParams(templateParams) {
      if (!this.templateMediaFile || !templateParams) {
        return;
      }

      const blob = await this.directUploadFile(this.templateMediaFile);
      const mediaUrl = this.buildTemplateMediaUrl(blob);

      const processed = templateParams.processed_params || {};
      const header = processed.header || {};
      header.media_url = mediaUrl;
      header.media_type = header.media_type || this.deriveTemplateMediaType(this.templateMediaFile);
      header.media_name = header.media_name || blob.filename;
      delete header.media_source;
      delete header.media_size;
      processed.header = header;
      templateParams.processed_params = processed;

      this.templateMediaFile = null;
    },
    async validateForm() {
      this.errors = [];

      if (!this.scheduledAt) {
        this.errors.push('Selecione uma data e hora');
        return false;
      }

      // Convert chosen wall time in selected timezone to UTC and compare
      const scheduledUtc = this.getScheduledAtUtc();
      this.scheduledAtUtc = scheduledUtc;
      if (!scheduledUtc || scheduledUtc <= new Date()) {
        await this.$refs.confirmDialog.showConfirmation();
        return false;
      }

      // Template validation (WhatsApp only)
      if (this.isWhatsAppInbox && (this.templateMode || this.mustUseTemplate)) {
        if (!this.selectedTemplate) {
          this.errors.push('Selecione um template do WhatsApp');
          return false;
        }

        // Template parameters are already validated by the WhatsappTemplatesModal
        // The modal won't emit the onSend event unless all parameters are filled
      } else {
        // Regular message validation (only when not using template)
        if (!this.content && this.attachments.length === 0) {
          this.errors.push('Adicione conteúdo ou anexo');
          return false;
        }
      }

      // Não validar mais signed_ids faltando - arquivos pendentes serão enviados no submit

      if (this.hasAudioAttachment && (this.content || this.attachments.length > 1)) {
        this.errors.push('Áudio não pode ser combinado com texto ou outros anexos');
        return false;
      }

      if (this.attachments.length > 1) {
        this.errors.push('Máximo de 1 anexo permitido');
        return false;
      }

      if (this.isWeeklyRecurrence && this.selectedDaysOfWeek.length === 0) {
        this.errors.push('Selecione pelo menos um dia da semana');
        return false;
      }

      if (this.isMonthlyRecurrence && (this.dayOfMonth < 1 || this.dayOfMonth > 31)) {
        this.errors.push('Dia do mês deve ser entre 1 e 31');
        return false;
      }

      if (this.isCustomInterval && this.intervalDays < 1) {
        this.errors.push('Intervalo deve ser de pelo menos 1 dia');
        return false;
      }

      if (this.isBroadcast && this.selectedSegmentIds.length === 0 && this.selectedContactIds.length === 0) {
        this.errors.push('Selecione segmentos ou contatos para o disparo');
        return false;
      }

      return true;
    },
    buildRecurrenceConfig() {
      if (this.recurrenceType === RECURRENCE_TYPES.NO_RECURRENCE) {
        return {};
      }

      const config = {
        chosen_time: this.getChosenTimeInTimezone(),
      };

      if (this.isWeeklyRecurrence) {
        config.days_of_week = this.selectedDaysOfWeek;
      } else if (this.isMonthlyRecurrence) {
        config.day_of_month = this.dayOfMonth;
      } else if (this.isCustomInterval) {
        config.interval_days = this.intervalDays;
      }

      return config;
    },
    buildTargetPayload() {
      if (!this.isBroadcast) {
        return {};
      }

      return {
        target_type: 'segment',
        inbox_id: this.inboxId,
        segment_ids: this.selectedSegmentIds,
        contact_ids: this.selectedContactIds,
      };
    },
    async scheduleMessage() {
      if (!(await this.validateForm())) {
        if (this.errors.length > 0) {
          useAlert(this.errors[0]);
        }
        return;
      }

      this.isSubmitting = true;
      this.errors = [];

      const scheduledUtc = this.scheduledAtUtc || this.getScheduledAtUtc();

      const data = {
        scheduled_at: scheduledUtc ? scheduledUtc.toISOString() : null,
        timezone: this.timezone,
        recurrence_type: this.recurrenceType,
        recurrence_config: this.buildRecurrenceConfig(),
        content_attributes: {},
        additional_attributes: {},
      };

      // Add template params if using template
      if (this.isWhatsAppInbox && (this.templateMode || this.mustUseTemplate)) {
        const templateParams = this.buildTemplateParams();
        data.additional_attributes.template_params = templateParams;

        // Content is optional when using templates, but we can keep the preview
        data.content = this.content || '';
        data.attachment_ids = [];
      } else {
        // Regular message
        data.content = this.content;
        // Apenas IDs de anexos que já têm signed_id (ex: vindos da ReplyBox)
        data.attachment_ids = this.attachmentSignedIds;
      }

      if (this.isBroadcast) {
        data.target_payload = this.buildTargetPayload();
        data.inbox_id = this.inboxId;
      }

      // Se o template tiver mídia local, fazer upload agora e embutir a URL
      if (this.templateMediaFile && data.additional_attributes.template_params) {
        try {
          await this.applyTemplateMediaToTemplateParams(
            data.additional_attributes.template_params
          );
        } catch (error) {
          console.error('[ScheduleMessageModal] Failed to upload template media', error);
          useAlert('Erro ao enviar a mídia do template. Tente novamente.');
          this.isSubmitting = false;
          return;
        }
      }

      // Extrair arquivos pendentes para envio via FormData (modo indireto)
      const pendingFiles = Object.values(this.pendingAttachmentFiles).filter(f => f instanceof File);

      try {
        const action = this.isBroadcast
          ? 'scheduledMessages/createBroadcast'
          : 'scheduledMessages/createForConversation';

        // Passar files junto com data para envio via FormData
        const payload = this.isBroadcast
          ? { data, files: pendingFiles }
          : { conversationId: this.conversationId, data, files: pendingFiles };

        await this.$store.dispatch(action, payload);

        useAlert(this.isBroadcast ? 'Disparo agendado com sucesso!' : 'Mensagem agendada com sucesso!');
        this.$emit('scheduled', { success: true });
        this.onClose();
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Erro ao agendar mensagem';
        this.errors.push(errorMessage);
        useAlert(errorMessage);
      } finally {
        this.isSubmitting = false;
      }
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        const fileWrapper = { file };
        this.onFileUpload(fileWrapper);
      }
    },
    onTemplateSelected(payload) {
      // Payload from WhatsappTemplatesModal has format:
      // { message: string, templateParams: { name, category, language, namespace, processed_params }, mediaFile?: File }

      if (!payload || !payload.templateParams) return;

      // Store the template parameters
      this.selectedTemplate = payload.templateParams;

      // Set the processed message content
      this.content = payload.message;

      // Store media file locally to upload only when scheduling
      this.templateMediaFile = payload.mediaFile instanceof File ? payload.mediaFile : null;

      // Activate template mode if not already
      this.templateMode = true;

      // Close the modal
      this.showWhatsappTemplatesModal = false;
    },
    buildTemplateParams() {
      if (!this.selectedTemplate) return null;

      const {
        name,
        category,
        language,
        namespace,
        processed_params: processedParams,
        footer,
        buttons,
        format_version: formatVersion,
      } = this.selectedTemplate;

      // Preserve the same shape used by envios imediatos (template modal)
      return {
        name,
        category,
        language,
        namespace: namespace || '',
        processed_params: processedParams || {},
        footer: footer || '',
        buttons: Array.isArray(buttons) ? buttons : [],
        ...(formatVersion ? { format_version: formatVersion } : {}),
      };
    },
    getScheduledAtUtc() {
      if (!this.scheduledAt || !this.timezone) return null;
      const pad = (num) => String(num).padStart(2, '0');
      const wallTime = `${this.scheduledAt.getFullYear()}-${pad(this.scheduledAt.getMonth() + 1)}-${pad(this.scheduledAt.getDate())} ${pad(this.scheduledAt.getHours())}:${pad(this.scheduledAt.getMinutes())}:00`;
      return zonedTimeToUtc(wallTime, this.timezone);
    },
    getChosenTimeInTimezone() {
      const scheduledUtc = this.scheduledAtUtc || this.getScheduledAtUtc();
      if (!scheduledUtc) return '00:00';
      const pad = (num) => String(num).padStart(2, '0');
      const localDate = new Date(
        scheduledUtc.toLocaleString('en-US', { timeZone: this.timezone })
      );
      return `${pad(localDate.getHours())}:${pad(localDate.getMinutes())}`;
    },
  },
};
</script>

<template>
  <woot-modal
    v-model:show="show"
    :on-close="onClose"
    :full-width="false"
    class="schedule-modal"
  >
    <div class="flex flex-col max-h-[85vh] md:max-h-[90vh]">
      <woot-modal-header
        :header-title="isBroadcast ? 'Agendar Disparo em Massa' : 'Agendar Mensagem'"
      />

      <form
        class="flex-1 w-full self-stretch overflow-y-auto px-6 py-4 space-y-3"
        @submit.prevent="scheduleMessage"
      >

        <!-- Date and Time Picker (Integrated) -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            <i class="i-lucide-calendar-clock mr-1"></i>
            Data e Horário *
          </label>
          <DateTimePicker
            class="w-full"
            :value="scheduledAt"
            format="DD-MM-YYYY HH:mm"
            :show-second="false"
            placeholder="Selecione data e hora"
            confirm-text="Confirmar"
            @change="scheduledAt = $event"
          />
        </div>

        <!-- Template Selection (WhatsApp Only) -->
        <div v-if="isWhatsAppInbox">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            <i class="i-lucide-file-text mr-1"></i>
            Template WhatsApp
          </label>

          <!-- Template Button and Selected Template Card -->
          <div>
            <!-- Selected Template Card -->
            <div
              v-if="selectedTemplate"
              class="border border-n-brand rounded-lg overflow-hidden bg-n-iris-1 dark:bg-n-slate-3"
            >
              <div class="p-3 bg-n-iris-2 dark:bg-n-slate-4 border-b border-n-brand flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <i class="i-ph-whatsapp-logo text-xl text-n-iris-9 dark:text-n-iris-5"></i>
                  <div>
                    <div class="font-medium text-n-iris-11 dark:text-white">{{ selectedTemplate?.name }}</div>
                    <div class="text-xs text-n-iris-9 dark:text-n-slate-2">{{ selectedTemplate?.language }} • {{ selectedTemplate?.category }}</div>
                  </div>
                </div>
                <button
                  type="button"
                  class="px-3 py-1 text-sm rounded-md border border-n-iris-6 bg-white dark:bg-n-slate-6 hover:bg-n-iris-1 dark:hover:bg-n-slate-5 text-n-iris-11 dark:text-white transition-colors"
                  @click="showWhatsappTemplatesModal = true"
                >
                  Alterar
                </button>
              </div>

              <div class="p-3 bg-white dark:bg-n-slate-2">
                <h4 class="text-sm font-medium text-n-slate-11 dark:text-white mb-2">Preview:</h4>
                <div class="text-sm text-n-slate-12 dark:text-white whitespace-pre-line">
                  {{ content }}
                </div>
              </div>
            </div>
            <!-- Select Template Button -->
            <button
              v-else
              type="button"
              class="w-full p-4 rounded-lg border-2 border-dashed border-n-slate-6 hover:border-n-iris-9 hover:bg-n-iris-1 transition-all flex items-center justify-center gap-2"
              @click="showWhatsappTemplatesModal = true"
            >
              <i class="i-ph-whatsapp-logo text-2xl text-n-iris-9"></i>
              <span class="text-sm font-medium text-n-slate-12">Selecionar Template do WhatsApp</span>
            </button>
          </div>
        </div>

        <!-- Message Content with Drag & Drop -->
        <div v-if="!templateMode || !isWhatsAppInbox">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            <i class="i-lucide-message-square mr-1"></i>
            Mensagem
          </label>

          <div
            ref="messageArea"
            class="relative w-full rounded-lg border border-n-weak bg-n-solid-1 overflow-hidden"
          >
            <!-- Drag & Drop Overlay -->
            <div
              v-if="isDragging"
              class="absolute inset-0 z-50 flex items-center justify-center pointer-events-none"
            >
              <div class="absolute inset-0 bg-woot-50/90 dark:bg-woot-900/90 rounded-lg border-2 border-dotted border-woot-600"></div>
              <div class="relative text-center">
                <div class="mb-4 flex justify-center">
                  <i class="i-lucide-upload-cloud text-6xl text-woot-600" />
                </div>
                <p class="font-semibold text-xl text-woot-600">
                  Solte arquivos aqui
                </p>
              </div>
            </div>

            <!-- File Preview Area (like WhatsApp) -->
            <div v-if="attachments.length > 0" class="relative">
              <div
                v-for="(attachment, index) in attachments"
                :key="attachment.id"
                class="relative"
              >
                <template v-if="isImageFile(attachment.resource?.type || attachment.resource?.content_type)">
                  <img
                    :src="attachment.thumb"
                    alt="Anexo"
                    class="w-full max-h-64 object-cover"
                  />
                </template>
                <template v-else-if="isVideoFile(attachment.resource?.type || attachment.resource?.content_type)">
                  <video
                    :src="attachment.thumb"
                    class="w-full max-h-64 object-cover"
                    controls
                  />
                </template>
                <template v-else-if="isAudioFile(attachment.resource?.type || attachment.resource?.content_type)">
                  <div
                    class="flex items-center gap-3 p-4 bg-n-slate-1 dark:bg-n-slate-2 rounded-t-lg"
                  >
                    <div
                      class="w-12 h-12 flex items-center justify-center bg-n-slate-3 dark:bg-n-slate-7 rounded"
                    >
                      <i
                        class="i-lucide-volume-2 text-n-slate-9 dark:text-n-slate-11 text-xl"
                      />
                    </div>
                    <div class="flex-grow">
                      <div class="font-medium text-n-slate-12 dark:text-n-slate-12">
                        {{ attachment.resource?.name || 'Áudio' }}
                      </div>
                      <div class="text-sm text-n-slate-9 dark:text-n-slate-11">
                        {{ formatFileSize(attachment.resource?.size || 0) }}
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div
                    class="flex items-center gap-3 p-4 bg-n-slate-1 dark:bg-n-slate-2 rounded-t-lg"
                  >
                    <div
                      class="w-12 h-12 flex items-center justify-center bg-n-slate-3 dark:bg-n-slate-7 rounded"
                    >
                      <i
                        class="i-lucide-file text-n-slate-9 dark:text-n-slate-11 text-xl"
                      />
                    </div>
                    <div class="flex-grow">
                      <div class="font-medium text-n-slate-12 dark:text-n-slate-12">
                        {{ attachment.resource?.name || 'Arquivo' }}
                      </div>
                      <div class="text-sm text-n-slate-9 dark:text-n-slate-11">
                        {{ formatFileSize(attachment.resource?.size || 0) }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Remove button -->
                <button
                  type="button"
                  class="absolute top-2 right-2 w-9 h-9 bg-n-ruby-9 hover:bg-n-ruby-10 text-white rounded-full flex items-center justify-center transition-all shadow-lg hover:shadow-xl hover:scale-110"
                  title="Remover arquivo"
                  @click="removeAttachment(index)"
                >
                  <i class="i-lucide-trash-2 text-base" />
                </button>
              </div>
            </div>

            <!-- Message Text Editor -->
            <div class="schedule-modal-editor">
              <WhatsAppEditor
                ref="messageInputRef"
                v-model="content"
                :disabled="hasAudioAttachment"
                :placeholder="hasAudioAttachment ? 'Mensagens de áudio não permitem texto adicional' : (attachments.length > 0 ? 'Adicione uma legenda...' : 'Digite sua mensagem...')"
                :template-variables="templateVariables"
                :has-audio-attachment="hasAudioAttachment"
                :has-selected-variations="false"
                :show-template-variables="showTemplateVariables"
                :show-emoji-picker="showEmojiPicker"
                show-flag-preview
                @toggle-variables="showTemplateVariables = !showTemplateVariables"
                @toggle-emoji="showEmojiPicker = !showEmojiPicker"
                @attach-file="$refs.fileInput.click()"
              />
            </div>

            <!-- Hidden file input -->
            <input
              ref="fileInput"
              type="file"
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
              class="hidden"
              @change="handleFileChange"
            />
          </div>

          <p v-if="hasAudioAttachment" class="text-n-amber-11 text-sm mt-2 flex items-center gap-1">
            <i class="i-lucide-alert-circle"></i>
            Áudio não pode ser combinado com texto (restrição WhatsApp)
          </p>
        </div>

        <!-- Advanced Section (Collapsible) -->
        <div>
          <button
            type="button"
            class="flex items-center gap-1.5 text-sm font-medium text-n-slate-11 hover:text-n-slate-12"
            @click="showAdvanced = !showAdvanced"
          >
            <i :class="showAdvanced ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"></i>
            Configurações Avançadas
          </button>

          <div v-if="showAdvanced" class="mt-2 pl-6">
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              <i class="i-lucide-globe mr-1"></i>
              Fuso Horário
            </label>
            <select
              v-model="timezone"
              class="h-10 w-full bg-n-alpha-2 border border-n-weak hover:border-n-slate-6 focus:border-n-brand rounded-lg px-3 text-sm text-n-slate-12 transition-colors"
            >
              <option
                v-for="tz in timezoneOptions"
                :key="tz.value"
                :value="tz.value"
              >
                {{ tz.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Broadcast Target (if broadcast) -->
        <div v-if="isBroadcast" class="p-3 bg-n-amber-2 border border-n-amber-6 rounded-lg">
          <p class="text-sm text-n-slate-11 italic">
            <i class="i-lucide-users mr-1"></i>
            Seleção de segmentos/contatos será integrada posteriormente
          </p>
        </div>

        <!-- Error Display -->
        <div v-if="errors.length > 0">
          <div
            v-for="(error, index) in errors"
            :key="index"
            class="bg-n-ruby-2 border border-n-ruby-6 text-n-ruby-11 px-3 py-2 rounded-lg text-sm"
          >
            <i class="i-lucide-alert-circle mr-1"></i>
            {{ error }}
          </div>
        </div>
      </form>

      <!-- Action Buttons (Sticky Footer) -->
      <div class="flex justify-end gap-2 px-6 py-4 border-t border-n-weak bg-n-solid-1">
        <woot-button
          variant="clear"
          size="small"
          @click.prevent="onClose"
        >
          Cancelar
        </woot-button>
        <woot-button
          type="button"
          size="small"
          :disabled="isSubmitting"
          @click="scheduleMessage"
        >
          <i class="i-lucide-clock mr-1.5 text-sm"></i>
          {{ submitButtonText }}
        </woot-button>
      </div>
    </div>
  </woot-modal>

  <!-- Warning modal for past date -->
  <ConfirmationModal
    ref="confirmDialog"
    title="Horário no passado"
    description="A data e hora de agendamento deve ser no futuro. Por favor, selecione uma data e hora posterior ao momento atual."
    confirm-label="OK"
    :cancel-label="null"
  />

  <!-- WhatsApp Templates Modal -->
  <WhatsappTemplatesModal
    v-if="isWhatsAppInbox"
    :show="showWhatsappTemplatesModal"
    :inbox-id="inboxId"
    mode="select"
    @on-send="onTemplateSelected"
    @cancel="showWhatsappTemplatesModal = false"
    @close="showWhatsappTemplatesModal = false"
  />
</template>

<style scoped>
.schedule-modal :deep(.modal-container) {
  max-width: 850px;
}

/* Let the form and its children stretch to the modal width */
.schedule-modal :deep(.modal-container form) {
  width: 100%;
  max-width: 100%;
  align-self: stretch;
}

/* Make DateTimePicker fill available width */
.schedule-modal :deep(.date-picker),
.schedule-modal :deep(.mx-datepicker),
.schedule-modal :deep(.mx-input-wrapper),
.schedule-modal :deep(.mx-input) {
  width: 100% !important;
  max-width: 100%;
  display: block;
}

/* Optimize WhatsApp Editor for modal context */
.schedule-modal-editor :deep(.whatsapp-editor) {
  width: 100%;
  max-width: 100%;
  /* Reduce textarea height for modal */
  textarea {
    min-height: 100px !important;
    max-height: 250px !important;
  }
}

/* Hide AI variations button and variables button in schedule modal */
.schedule-modal-editor :deep(.ai-button),
.schedule-modal-editor :deep(button:has(.i-lucide-sparkles)),
.schedule-modal-editor :deep(button:has(.i-lucide-list)),
.schedule-modal-editor :deep(button:has(.i-lucide-braces)) {
  display: none !important;
}

/* Make toolbar more compact */
.schedule-modal-editor :deep(.toolbar) {
  padding: 0.5rem;
  gap: 0.25rem;
  flex-wrap: wrap;
}

/* Ensure dropdowns and inputs don't have overflow issues */
select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}

@media (max-width: 768px) {
  .schedule-modal :deep(.modal-container) {
    max-width: 100%;
    width: 100%;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }

  .schedule-modal :deep(.modal-content) {
    height: 100%;
  }
}
</style>
